import axios from 'axios';
import { urlMyspreadApi } from '../urls';
import { basicAuthConfig } from '../configs';

/**
 * Obtiene los permisos del usuario conectado
 * @param {Object} currentUser - Usuario actual conectado
 * @returns {Promise<Object>} - Response con los permisos del usuario
 */
export const getUserPermissions = async (currentUser) => {
    const url = `${urlMyspreadApi}mantenedores/usuarios/permisos/me`;
    
    try {
        const response = await axios.get(url, basicAuthConfig(currentUser));
        
        if (response.status === 200) {
            return {
                success: true,
                data: response.data
            };
        }
        
        return {
            success: false,
            error: 'Error al obtener los permisos del usuario'
        };
    } catch (error) {
        console.error('Error fetching user permissions:', error);
        return {
            success: false,
            error: error.response ? error.response.data.message : 'Error desconocido al obtener permisos'
        };
    }
};

/**
 * Procesa los permisos raw del API y los convierte en un formato más utilizable
 * @param {Object} permissionsData - Data cruda del API
 * @returns {Object} - Permisos procesados
 */
export const processUserPermissions = (permissionsData) => {
    if (!permissionsData || !permissionsData.permisos) {
        return {
            email: null,
            userId: null,
            userUid: null,
            hasAnyPermissions: false,
            modules: {},
            rawPermissions: {},
            timestamp: null
        };
    }

    const { email, permisos, usuario, usuario_id } = permissionsData;
    
    // Mapeo de módulos del API a nuestros mantenedores
    const moduleMapping = {
        'cierre': 'cierre',
        'conductores': 'directorio',
        'empresas': 'empresas',
        'extras': 'extras',
        'mantenedores_generales': 'generales',
        'rutas': 'rutas',
        'tarifarios': 'tarifarios',
        'usuarios': 'usuarios',
        'estados_subestados': 'estados', // Por si existe en el futuro
        'estados': 'estados' // Agregar mapeo directo también
    };

    // Procesar permisos por módulo
    const processedModules = {};
    const hasAnyPermissions = Object.keys(permisos).length > 0;

    // Si tiene algún permiso en mantenedores, agregar acceso general al módulo mantenedores
    if (hasAnyPermissions) {
        processedModules['mantenedores'] = {
            hasAccess: true,
            permissions: ['vista'], // Acceso básico al módulo principal
            canView: true,
            canCreate: false,
            canEdit: false,
            canDelete: false,
            canAdmin: false,
            canApprove: false,
            canRequest: false,
            originalKey: 'mantenedores_module'
        };
    }

    Object.entries(permisos).forEach(([moduleKey, permissions]) => {
        const mappedModule = moduleMapping[moduleKey] || moduleKey;
        
        processedModules[mappedModule] = {
            hasAccess: permissions && permissions.length > 0,
            permissions: permissions || [],
            canView: permissions.includes('vista'),
            canCreate: permissions.includes('crear'),
            canEdit: permissions.includes('editar'),
            canDelete: permissions.includes('eliminar'),
            canAdmin: permissions.includes('administrar'),
            canApprove: permissions.includes('aprobar'),
            canRequest: permissions.includes('solicitar'),
            originalKey: moduleKey
        };
    });

    return {
        email,
        userId: usuario_id,
        userUid: usuario,
        hasAnyPermissions,
        modules: processedModules,
        rawPermissions: permisos,
        timestamp: new Date().toISOString()
    };
};

/**
 * Verifica si el usuario tiene acceso a un módulo específico
 * @param {Object} processedPermissions - Permisos procesados
 * @param {string} moduleId - ID del módulo (ej: 'directorio', 'empresas')
 * @returns {boolean} - true si tiene acceso
 */
export const hasModuleAccess = (processedPermissions, moduleId) => {
    if (!processedPermissions || !processedPermissions.modules) {
        return false;
    }
    
    const modulePerms = processedPermissions.modules[moduleId];
    return modulePerms ? modulePerms.hasAccess : false;
};

/**
 * Verifica si el usuario tiene un permiso específico en un módulo
 * @param {Object} processedPermissions - Permisos procesados
 * @param {string} moduleId - ID del módulo
 * @param {string} permission - Tipo de permiso ('vista', 'crear', 'editar', 'eliminar', 'administrar')
 * @returns {boolean} - true si tiene el permiso
 */
export const hasModulePermission = (processedPermissions, moduleId, permission) => {
    if (!hasModuleAccess(processedPermissions, moduleId)) {
        return false;
    }
    
    const modulePerms = processedPermissions.modules[moduleId];
    
    switch (permission) {
        case 'vista':
            return modulePerms.canView;
        case 'crear':
            return modulePerms.canCreate;
        case 'editar':
            return modulePerms.canEdit;
        case 'eliminar':
            return modulePerms.canDelete;
        case 'administrar':
            return modulePerms.canAdmin;
        case 'aprobar':
            return modulePerms.canApprove;
        case 'solicitar':
            return modulePerms.canRequest;
        default:
            return modulePerms.permissions.includes(permission);
    }
};

/**
 * Obtiene la lista de módulos a los que el usuario tiene acceso
 * @param {Object} processedPermissions - Permisos procesados
 * @returns {Array} - Array de IDs de módulos con acceso
 */
export const getAccessibleModules = (processedPermissions) => {
    if (!processedPermissions || !processedPermissions.modules) {
        return [];
    }
    
    return Object.entries(processedPermissions.modules)
        .filter(([moduleId, modulePerms]) => modulePerms.hasAccess)
        .map(([moduleId]) => moduleId);
};

/**
 * Hook personalizado para manejar permisos de usuario (para usar en componentes)
 * @param {Object} currentUser - Usuario actual
 * @returns {Object} - Estado y funciones de permisos
 */
export const createPermissionsManager = (currentUser) => {
    return {
        async loadPermissions() {
            const response = await getUserPermissions(currentUser);
            if (response.success) {
                return processUserPermissions(response.data);
            }
            return null;
        },
        
        hasAccess: (processedPermissions, moduleId) => hasModuleAccess(processedPermissions, moduleId),
        hasPermission: (processedPermissions, moduleId, permission) => hasModulePermission(processedPermissions, moduleId, permission),
        getAccessibleModules: (processedPermissions) => getAccessibleModules(processedPermissions)
    };
};
